
import { useState, useCallback } from 'react';
import { safeToastError, safeToastInfo, safeToastSuccess } from '@/components/ui/toastManager';
import OfflineLogger from '@/utils/offlineLogger';
import { networkService } from '@/services/network.service';

interface ErrorHandlerOptions {
  maxRetries?: number;
  retryDelay?: number;
  showToast?: boolean;
  logError?: boolean;
  fallbackAction?: () => void;
}

interface UseEnhancedErrorHandlerReturn {
  handleError: (error: any, context: string, options?: ErrorHandlerOptions) => void;
  handleAsyncAction: <T>(
    action: () => Promise<T>,
    context: string,
    options?: ErrorHandlerOptions & {
      successMessage?: string;
      loadingMessage?: string;
    }
  ) => Promise<T | null>;
  isLoading: boolean;
  retryCount: number;
}

export function useEnhancedErrorHandler(): UseEnhancedErrorHandlerReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const handleError = useCallback((
    error: any, 
    context: string, 
    options: ErrorHandlerOptions = {}
  ) => {
    const {
      showToast = true,
      logError = true,
      fallbackAction
    } = options;

    if (logError) {
      OfflineLogger.error(`useEnhancedErrorHandler.${context}`, error);
    }

    // Determina il messaggio di errore user-friendly
    let errorMessage = 'Si è verificato un errore imprevisto';
    
    if (!networkService.isOnline()) {
      errorMessage = 'Operazione non disponibile in modalità offline';
    } else if (error?.message?.includes('fetch')) {
      errorMessage = 'Errore di connessione al server';
    } else if (error?.message) {
      errorMessage = error.message;
    }

    if (showToast) {
      safeToastError(errorMessage);
    }

    // Esegui fallback se disponibile
    if (fallbackAction) {
      try {
        fallbackAction();
      } catch (fallbackError) {
        OfflineLogger.error(`useEnhancedErrorHandler.${context}.fallback`, fallbackError);
      }
    }
  }, []);

  const handleAsyncAction = useCallback(async <T>(
    action: () => Promise<T>,
    context: string,
    options: ErrorHandlerOptions & {
      successMessage?: string;
      loadingMessage?: string;
    } = {}
  ): Promise<T | null> => {
    const {
      maxRetries = 2,
      retryDelay = 1000,
      successMessage,
      loadingMessage,
      ...errorOptions
    } = options;

    setIsLoading(true);
    setRetryCount(0);

    if (loadingMessage) {
      safeToastInfo(loadingMessage);
    }

    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await action();
        
        if (successMessage) {
          safeToastSuccess(successMessage);
        }
        
        setRetryCount(0);
        setIsLoading(false);
        return result;
      } catch (error) {
        lastError = error;
        setRetryCount(attempt + 1);
        
        OfflineLogger.warn(`useEnhancedErrorHandler.${context}`, `Attempt ${attempt + 1} failed:`, error);
        
        // Se non è l'ultimo tentativo, aspetta prima di riprovare
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
        }
      }
    }

    // Tutti i tentativi falliti
    setIsLoading(false);
    handleError(lastError, context, errorOptions);
    return null;
  }, [handleError]);

  return {
    handleError,
    handleAsyncAction,
    isLoading,
    retryCount
  };
}
